import { __assign, __read, __rest } from "tslib";
import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import uniqueId from 'lodash/uniqueId';
import { DeleteCircle as DeleteCircleIcon } from '@ringcentral/juno-icon';
import { combineProps, useA11yKeyEvent, useEventCallback, useFocusInside, } from '../../../../../foundation';
import { RcIconButton } from '../../../../Buttons/IconButton';
import { RcVisuallyHidden } from '../../../../VisuallyHidden';
import { StyledPickerTextField, StyledPopover } from '../../styles';
var popoverAnchorOrigin = {
    vertical: 'bottom',
    horizontal: 'left',
};
var popoverTransformOrigin = {
    vertical: 'top',
    horizontal: 'left',
};
var PICKER_DISPLAY_NAME = 'PickerTextField';
var PickerTextField = forwardRef(function (props, ref) {
    var onClear = props.onClear, InputProps = props.InputProps, disabled = props.disabled, action = props.action, onKeyDown = props.onKeyDown, _a = props.inputProps, _b = _a === void 0 ? {} : _a, announcementText = _b.announcementText, inputProps = __rest(_b, ["announcementText"]), PopoverPropsProp = props.PopoverProps, children = props.children, ActionSymbol = props.ActionSymbol, clearBtn = props.clearBtn, clearButtonProps = props.clearButtonProps, value = props.value, rest = __rest(props, ["onClear", "InputProps", "disabled", "action", "onKeyDown", "inputProps", "PopoverProps", "children", "ActionSymbol", "clearBtn", "clearButtonProps", "value"]);
    var focusInside = useFocusInside();
    var idForInput = useRef(uniqueId(PICKER_DISPLAY_NAME + "-")).current;
    var _c = __read(useState(null), 2), anchorEl = _c[0], setAnchorEl = _c[1];
    var idForHelperText = idForInput + "-helper-text";
    var idForInstruction = idForInput + "-instruction";
    var handlePickerClick = useEventCallback(function (event) {
        if (!disabled && !anchorEl) {
            setAnchorEl(event.currentTarget);
        }
    });
    var handleA11yKeydown = useA11yKeyEvent(function (event) {
        setAnchorEl(event.currentTarget);
    });
    var handleKeydown = useEventCallback(function (event) {
        if (disabled)
            return;
        handleA11yKeydown(event);
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(event);
    });
    var handleClearClick = useEventCallback(function (e) {
        e.stopPropagation();
        e.preventDefault();
        onClear === null || onClear === void 0 ? void 0 : onClear(e);
    });
    var _InputProps = useMemo(function () {
        var iconTitle = clearButtonProps === null || clearButtonProps === void 0 ? void 0 : clearButtonProps.title;
        var combineClearProps = clearBtn &&
            combineProps({
                TooltipProps: {
                    tooltipForceHide: !iconTitle,
                },
                onClick: handleClearClick,
            }, clearButtonProps);
        return combineProps({
            readOnly: true,
            endAdornment: (React.createElement(React.Fragment, null,
                value && (React.createElement(RcIconButton, __assign({ className: "picker-clear", variant: "plain", tabIndex: -1, disabled: disabled, symbol: DeleteCircleIcon, title: iconTitle, "aria-label": iconTitle }, combineClearProps))),
                React.createElement(RcIconButton, { "aria-hidden": "true", className: "picker-action", variant: "plain", tabIndex: -1, disabled: disabled, symbol: ActionSymbol }))),
            onClick: handlePickerClick,
            onKeyDown: handleKeydown,
            inputProps: __assign({ 'aria-describedby': idForHelperText + " " + idForInstruction, 'aria-haspopup': true }, inputProps),
        }, InputProps);
    }, [
        clearBtn,
        handleClearClick,
        clearButtonProps,
        value,
        disabled,
        ActionSymbol,
        handlePickerClick,
        handleKeydown,
        idForHelperText,
        idForInstruction,
        inputProps,
        InputProps,
    ]);
    var _FormHelperTextProps = useMemo(function () { return ({
        id: idForHelperText,
    }); }, [idForHelperText]);
    var onPickerViewClose = useEventCallback(function () {
        setAnchorEl(null);
    });
    var PopoverProps = useMemo(function () {
        return combineProps({
            onClose: onPickerViewClose,
        }, PopoverPropsProp);
    }, [PopoverPropsProp, onPickerViewClose]);
    var popoverOpen = Boolean(anchorEl);
    useImperativeHandle(action, function () { return ({
        close: onPickerViewClose,
    }); }, [onPickerViewClose]);
    return (React.createElement(React.Fragment, null,
        React.createElement(StyledPickerTextField, __assign({ id: idForInput, ref: ref, InputProps: _InputProps, FormHelperTextProps: _FormHelperTextProps, focused: popoverOpen ? true : undefined, disabled: disabled, clearBtn: clearBtn, value: value }, rest)),
        React.createElement(StyledPopover, __assign({ open: popoverOpen, anchorEl: anchorEl, onClose: onPickerViewClose, anchorOrigin: popoverAnchorOrigin, transformOrigin: popoverTransformOrigin }, PopoverProps),
            focusInside.start,
            children,
            focusInside.end),
        React.createElement(RcVisuallyHidden, { id: idForInstruction }, announcementText)));
});
PickerTextField.displayName = PICKER_DISPLAY_NAME;
export { PickerTextField };
